import React, { forwardRef } from "react";

import { useAuthLogout, useCurrentUser, isRightAllowed, UserRight } from "@keepeek/commons";
import { BaseComponentProps } from "@keepeek/refront-components";
import { Box, Button, ClickAwayListener, Paper } from "@mui/material";
import { styled } from "@mui/material/styles";
import clsx from "clsx";
import Link from "next/link";
import { useTranslation } from "react-i18next";
import { useSetRecoilState } from "recoil";

import { KIcon } from "../../components/common/KIcon";
import { CONFIGURATION_SECTION_PATH } from "../../components/layout/adminMenu";
import { FrontEdit } from "../../models/configuration/components/frontEdit";
import { Import } from "../../models/configuration/components/import";
import { AutoGuestLogin, Global } from "../../models/configuration/global";
import { useConfiguration } from "../../providers/config/hooks";
import { useInitConfig } from "../../providers/config/hooks/init";
import { useBackdoors } from "../../providers/config/hooks/useBackdoors";
import { QUERY_BACKDOOR } from "../../providers/config/models";
import useFrontEdit from "../../providers/frontEdit/hooks/useFrontEdit";
import { importModalOpenState } from "../../providers/import/atoms";
import { useDidomi } from "../../providers/tools/didomi/hooks";
import { KHeaderWidgetComponentContext } from "../widgets/KHeaderWidget/models";

const PREFIX = "CurrentUserPopup";

const classes = {
  root: `${PREFIX}-root`,
  list: `${PREFIX}-list`,
  listItem: `${PREFIX}-listItem`,
  listItemLogout: `${PREFIX}-listItemLogout`,
  button: `${PREFIX}-button`,
  icon: `${PREFIX}-icon`,
};

const StyledPaper = styled(Paper)<{
  context: KHeaderWidgetComponentContext;
}>(({ theme, context = KHeaderWidgetComponentContext.All }) => ({
  [`&.${classes.root}`]: {},
  [`& .${classes.list}`]: {
    padding: 0,
    listStyle: "none",
    margin: context === KHeaderWidgetComponentContext.Burger ? 0 : "",
  },

  [`& .${classes.listItem}`]: {
    "& > *:is(button)": {},
  },

  [`& .${classes.listItemLogout}`]: {
    backgroundColor:
      context === KHeaderWidgetComponentContext.Burger ? "" : theme.palette.grey[300],
    borderRadius: `0 0 ${theme.shape.borderRadius}px ${theme.shape.borderRadius}px`,
    borderTop:
      context === KHeaderWidgetComponentContext.Burger
        ? `none`
        : `1px solid ${theme.palette.grey[400]}`,
  },

  [`& .${classes.button}`]: {
    justifyContent: "flex-start",
    textAlign: "left",
    textTransform: "none",
    padding:
      context === KHeaderWidgetComponentContext.Burger ? theme.spacing(2, 1) : theme.spacing(2),
    "&:hover, &:focus": {
      backgroundColor: "rgba(0,0,0,0.08)",
    },
    "&:focus-within": {
      outline: `1px solid ${theme.palette.primary.main}`,
      outlineOffset: "0",
    },
  },
}));

type CurrentUserPopupProps = {
  onClose: () => void;
  open: boolean;
  context: KHeaderWidgetComponentContext;
  cookieConsentManagement: boolean;
} & BaseComponentProps;

const CurrentUserPopup = forwardRef<HTMLDivElement, CurrentUserPopupProps>(
  (
    { onClose, className, id, testId = "CurrentUserPopup", sx, context, cookieConsentManagement },
    ref,
  ) => {
    const { t } = useTranslation();
    const { logout } = useAuthLogout();
    const { isTech, hasImportRights, right, isPublicVisitor } = useCurrentUser();
    const { didomi, needDidomi } = useDidomi();
    const importConfig = useConfiguration<Import>(CONFIGURATION_SECTION_PATH.COMPONENTS_IMPORT);
    const frontEditConfig = useConfiguration<FrontEdit>(
      CONFIGURATION_SECTION_PATH.COMPONENTS_FRONT_EDIT,
    );
    const setOpenImportModal = useSetRecoilState(importModalOpenState);
    const { frontEdit, setFrontEdit } = useFrontEdit();
    const { initConfig } = useInitConfig();

    const displayFrontEditOption =
      frontEditConfig?.activate && isRightAllowed(right, UserRight.ADMINISTRATOR);

    const { displayUserLogoutInHeader } = useBackdoors();
    const global = useConfiguration<Global>(CONFIGURATION_SECTION_PATH.GLOBAL);

    /**
     * Determines whether to hide the logout button for public visitors based on the Keycloak configuration.
     * @type {boolean}
     */
    const hideLogout: boolean =
      global?.keycloakConfiguration?.autoGuestLogin === AutoGuestLogin.AutoGuestLogin &&
      isPublicVisitor;

    /**
     * The type of logout for visitors based on the Keycloak configuration.
     * @type {string}
     */
    const visitorLogoutType = global?.keycloakConfiguration?.autoGuestLogin;
    /**
     * If we are in the cause of auto connection of the visitor user
     * But we're forcing the UI to display the logout button
     * then redirect the logout to the landing page without reconnecting as a visitor automaticaly
     * This is to avoid loop of auto connexion (and frustration)
     *
     * @returns {(string | undefined)}
     */
    const logoutVisitorRedirect = (): string | undefined => {
      switch (visitorLogoutType) {
        case AutoGuestLogin.AutoGuestLogin:
          return `${location.origin}?${QUERY_BACKDOOR.STAY_ON_LANDING_AUTH}`;
        case AutoGuestLogin.AutoGuestLoginWithParam:
          return `${location.origin}`;
        default:
          return undefined;
      }
    };

    return (
      <StyledPaper
        data-testid={testId}
        id={id}
        className={clsx("CurrentUserPopup", className, classes.root)}
        elevation={context === KHeaderWidgetComponentContext.Burger ? 0 : 2}
        ref={ref}
        sx={sx}
        context={context}
      >
        <ClickAwayListener onClickAway={onClose}>
          <Box component="ul" className={clsx(classes.list)}>
            {hasImportRights && importConfig?.activate && (
              <Box component="li" className={clsx(classes.listItem)}>
                <Button
                  fullWidth
                  variant="text"
                  color="default"
                  disableFocusRipple
                  disableRipple
                  title={t("layout.import-button.label") as string}
                  data-testid="CurrentUserMenu-import"
                  className={clsx("CurrentUserPopup-button-import", classes.button) as string}
                  onClick={() => setOpenImportModal(true)}
                >
                  <KIcon
                    className={"CurrentUserPopup-icon fa-solid fa-up-from-line fa-lg"}
                    sx={{ paddingRight: "8px" }}
                  />
                  {t("layout.import-button.label")} {}
                </Button>
              </Box>
            )}
            {isTech && (
              <>
                <Box component="li" className={clsx(classes.listItem)}>
                  <Link href="/admin/global" passHref legacyBehavior>
                    <Button
                      component="a"
                      fullWidth
                      variant="text"
                      color="default"
                      disableFocusRipple
                      disableRipple
                      title={t("layout.admin-button.label") as string}
                      data-testid="CurrentUserMenu-admin"
                      className={clsx("CurrentUserPopup-button-admin", classes.button)}
                      onClick={() => {
                        setFrontEdit(false);
                      }}
                    >
                      <KIcon
                        className={"CurrentUserPopup-icon fa-solid fa-gear fa-lg"}
                        sx={{ paddingRight: "8px" }}
                      />
                      {t("layout.admin-button.label")}
                    </Button>
                  </Link>
                </Box>
              </>
            )}
            {displayFrontEditOption && (
              <Box
                sx={{ display: frontEdit ? "none" : "" }}
                component="li"
                className={clsx(classes.listItem)}
              >
                <Button
                  fullWidth
                  variant="text"
                  color="default"
                  disableFocusRipple
                  disableRipple
                  title={t("layout.front-edit-button.label") as string}
                  data-testid="CurrentUserMenu-front-edit"
                  className={clsx("CurrentUserPopup-button-front-edit", classes.button)}
                  onClick={() => {
                    setFrontEdit(true);
                  }}
                >
                  <KIcon
                    className={"CurrentUserPopup-icon fa-solid fa-pen fa-lg"}
                    sx={{ paddingRight: "8px" }}
                  />
                  {t("layout.front-edit-button.label")}
                </Button>
              </Box>
            )}
            {initConfig && cookieConsentManagement && needDidomi && (
              <Box component="li" className={clsx(classes.listItem)}>
                <Button
                  fullWidth
                  variant="text"
                  color="default"
                  disableFocusRipple
                  disableRipple
                  title={t("layout.consent-management.label") as string}
                  data-testid="CurrentUserMenu-consent-management"
                  className={clsx(
                    "CurrentUserPopup-button",
                    "CurrentUserPopup-button-consent-management",
                    classes.button,
                  )}
                  onClick={() => didomi?.preferences.show()}
                >
                  <KIcon
                    className={"CurrentUserPopup-icon fa-solid fa-square-user fa-lg"}
                    sx={{ paddingRight: "8px" }}
                  />
                  {t("layout.consent-management.label")}
                </Button>
              </Box>
            )}
            {(!hideLogout || displayUserLogoutInHeader) && (
              <Box component="li" className={clsx(classes.listItem, classes.listItemLogout)}>
                <Button
                  fullWidth
                  variant="text"
                  color="default"
                  disableFocusRipple
                  disableRipple
                  onClick={() => {
                    logout(logoutVisitorRedirect());
                  }}
                  title={t("layout.logout-button.label") as string}
                  data-testid="CurrentUserMenu-logout"
                  className={clsx("CurrentUserPopup-button-logout", classes.button)}
                >
                  <KIcon
                    className={"CurrentUserPopup-icon fa-regular fa-arrow-right-from-bracket fa-lg"}
                    sx={{ paddingRight: "8px" }}
                  />
                  {t("layout.logout-button.label")}
                </Button>
              </Box>
            )}
          </Box>
        </ClickAwayListener>
      </StyledPaper>
    );
  },
);

export default CurrentUserPopup;
